import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ISseEvent } from 'src/app/interfaces/isse-event';
import { IInternalSseEvent } from 'src/app/interfaces/iinternal-sse-event';
import { IConnectionInfo } from 'src/app/interfaces/iconnection-info';
import { MAX_CONCURRENT_CONNECTIONS, MAX_CONSECUTIVE_ERRORS } from 'src/app/utils/constants/sse/sse.constants';

@Injectable({
  providedIn: 'root',
})
export class SseService implements OnDestroy {
  private readonly connections = new Map<string, IConnectionInfo>();
  // Feature event types (e.g. 'generation_status_changed') registered per
  // connection, dynamically via on() — never hardcoded. Kept as a separate
  // map from `connections` so on() calls made before connect() still attach
  // once the socket opens, and so the same eventType isn't attached twice on
  // a still-live connection. Cleared alongside `connections` on disconnect()
  // / disconnectAll() — a context that reconnects after being explicitly
  // disconnected starts fresh and needs on() called again, so this map
  // doesn't grow unbounded for contexts that are gone for good.
  private readonly registeredEventTypes = new Map<string, Set<string>>();
  private eventsSubject = new Subject<IInternalSseEvent>();
  // Emits the connection key whenever a stream is permanently closed by the
  // infrastructure (non-200 response, consecutive-error cap). NOT emitted for
  // explicit disconnect() / disconnectAll() calls — those are caller-initiated
  // and the caller already knows the stream is done.
  private readonly closedSubject = new Subject<string>();
  private readonly sseBase = `${environment.backendUrl}/sse`;

  /**
   * Opens an SSE connection scoped to a specific context (e.g. simulation_generation + uuid).
   *
   * Safe to call multiple times for the same context — a still-open connection
   * is reused (idempotent). A CLOSED entry (e.g. from a previous permanent
   * failure) is replaced so the caller can reconnect after fix.
   *
   * Silently skips opening a new connection when the client cap
   * (MAX_CONCURRENT_CONNECTIONS) is reached — call isConnected() afterwards
   * to find out if the context is actually open.
   *
   * Multiple *different* contexts can be connected at the same time.
   *
   * Usage:
   *   this.sseService.connect('simulation_generation', generationUuid);
   *   this.sseService.on<IStatusEvent>('simulation_generation', generationUuid, 'generation_status_changed').subscribe(...)
   */
  connect(contextType: string, contextKey: string): void {
    const key = this.connectionKey(contextType, contextKey);

    // Treat a CLOSED entry as absent — it can't receive events and the browser
    // won't auto-retry it. Drop it so we can open a fresh stream below.
    const existing = this.connections.get(key);
    if (existing) {
      if (existing.eventSource.readyState !== EventSource.CLOSED) return; // still alive — idempotent
      existing.eventSource.close();
      this.connections.delete(key);
      this.registeredEventTypes.delete(key);
    }

    // Client-side cap: contexts over the limit fall back to polling.
    if (this.connections.size >= MAX_CONCURRENT_CONNECTIONS) {
      console.warn('[SseService] Client stream cap reached — not opening connection.', contextType, contextKey);
      return;
    }

    const url = `${this.sseBase}?contextType=${encodeURIComponent(contextType)}&contextKey=${encodeURIComponent(contextKey)}`;
    // withCredentials sends auth cookies cross-origin.
    const eventSource = new EventSource(url, { withCredentials: true });

    const info: IConnectionInfo = {
      eventSource,
      contextType,
      contextKey,
      consecutiveErrorCount: 0,
    };
    this.connections.set(key, info);

    eventSource.onmessage = (event: MessageEvent) => {
      this.dispatchEvent(key, 'message', event.data);
    };

    eventSource.onerror = () => {
      if (eventSource.readyState === EventSource.CLOSED) {
        // Permanent failure: a non-200 response (400/401/403/503/500) causes the
        // browser to set readyState to CLOSED after exactly one onerror and then
        // stop retrying. The MAX_CONSECUTIVE_ERRORS heuristic never trips for
        // this case because there is no subsequent CONNECTING retry. Drop the
        // entry and signal callers to fall back.
        console.warn('[SseService] Stream permanently closed (non-200 or rejected).', contextType, contextKey);
        this.connections.delete(key);
        this.registeredEventTypes.delete(key);
        this.closedSubject.next(key);
        return;
      }

      // readyState === CONNECTING: browser is auto-retrying after a transient
      // network blip; it will send Last-Event-ID so the server replays missed
      // events. Cap consecutive errors to avoid retrying forever against e.g.
      // a permanent 403 that somehow reaches this path.
      console.warn('[SseService] Connection error — browser will auto-reconnect with Last-Event-ID.', contextType, contextKey);
      info.consecutiveErrorCount++;
      if (info.consecutiveErrorCount >= MAX_CONSECUTIVE_ERRORS) {
        console.warn('[SseService] Too many consecutive errors — disconnecting instead of retrying further.', contextType, contextKey);
        this.disconnect(contextType, contextKey);
        this.closedSubject.next(key);
      }
    };

    // Re-attach whatever feature event types were already registered via
    // on() for this context before connect() opened the socket — a fresh
    // EventSource has no listeners yet. If this context was previously
    // disconnected, registeredEventTypes was cleared then, so this is a
    // no-op and on() must be called again.
    (this.registeredEventTypes.get(key) ?? new Set()).forEach(eventType =>
      this.attachFeatureListener(key, eventType),
    );

    // Scope timeout: BE closes the connection after a few minutes and sends
    // this event first. Do NOT call disconnect() here — EventSource
    // auto-reconnects and sends Last-Event-ID, which replays any events
    // missed during the brief reconnect window.
    eventSource.addEventListener('disconnecting', (event: MessageEvent) => {
      console.log('[SseService] Scope timeout — auto-reconnecting with Last-Event-ID.', contextType, contextKey);
      // dispatchEvent() below resets consecutiveErrorCount — this event
      // arriving is itself proof the connection is healthy.
      this.dispatchEvent(key, 'disconnecting', event.data);
    });
  }

  /**
   * Returns an Observable that emits (void) once when the stream for a given
   * context is permanently closed by the infrastructure — non-200 response or
   * consecutive-error cap — and the browser will not auto-reconnect. Callers
   * should fall back to polling on emission.
   *
   * NOT emitted for explicit disconnect() / disconnectAll() calls.
   */
  closed$(contextType: string, contextKey: string): Observable<void> {
    const key = this.connectionKey(contextType, contextKey);
    return this.closedSubject.asObservable().pipe(
      filter(k => k === key),
      map(() => undefined),
    );
  }

  /**
   * Returns an Observable of events for a specific context, optionally
   * filtered by event type. Subscribing to a new eventType here dynamically
   * registers a listener for it on that context's EventSource — SseService
   * never needs to know feature event names in advance.
   *
   * Pass a type param to get typed data instead of `unknown` — this is a
   * type-assertion contract (like JSON.parse), not a runtime check, so it's
   * on the caller to know the real shape the backend sends for that event.
   *
   * Called with no eventType, every event (of any name already subscribed
   * to) for that context arrives on one stream — so callers need to know
   * which one it was, hence the full { type, data } envelope instead of bare
   * data.
   *
   * Usage:
   *   this.sseService.on<IStatusEvent>('simulation_generation', generationUuid, 'generation_status_changed').subscribe(data => ...)
   *   this.sseService.on('simulation_generation', generationUuid).subscribe(event => ...)  // { type, data } for any registered event
   */
  on<T = unknown>(contextType: string, contextKey: string, eventType: string): Observable<T>;
  on(contextType: string, contextKey: string): Observable<ISseEvent>;
  on<T = unknown>(
    contextType: string,
    contextKey: string,
    eventType?: string,
  ): Observable<T | ISseEvent> {
    const key = this.connectionKey(contextType, contextKey);

    if (eventType) {
      const registered = this.registeredEventTypes.get(key) ?? new Set<string>();
      if (!registered.has(eventType)) {
        registered.add(eventType);
        this.registeredEventTypes.set(key, registered);
        this.attachFeatureListener(key, eventType);
      }
    }

    return this.eventsSubject.asObservable().pipe(
      filter(event => event.connectionKey === key && (!eventType || event.type === eventType)),
      map(event => (eventType ? (event.data as T) : { type: event.type, data: event.data })),
    );
  }

  isConnected(contextType: string, contextKey: string): boolean {
    const info = this.connections.get(this.connectionKey(contextType, contextKey));
    return !!info && info.eventSource.readyState !== EventSource.CLOSED;
  }

  /**
   * Closes the SSE connection for a specific context and forgets its
   * registered event types — reconnecting the same context afterwards
   * starts fresh and requires calling on() again.
   */
  disconnect(contextType: string, contextKey: string): void {
    const key = this.connectionKey(contextType, contextKey);
    const info = this.connections.get(key);
    if (info) {
      info.eventSource.close();
      this.connections.delete(key);
      this.registeredEventTypes.delete(key);
      console.log('[SseService] Connection closed.', contextType, contextKey);
    }
  }

  /**
   * Closes every active connection and forgets every registered event type —
   * for logout/invalid-session, where nothing should stay open or be
   * silently resumed regardless of context.
   */
  disconnectAll(): void {
    this.connections.forEach(info => {
      info.eventSource.close();
      console.log('[SseService] Connection closed.', info.contextType, info.contextKey);
    });
    this.connections.clear();
    this.registeredEventTypes.clear();
  }

  ngOnDestroy(): void {
    this.disconnectAll();
    this.eventsSubject.complete();
    this.closedSubject.complete();
  }

  private connectionKey(contextType: string, contextKey: string): string {
    return `${contextType}:${contextKey}`;
  }

  private attachFeatureListener(connectionKey: string, eventType: string): void {
    const info = this.connections.get(connectionKey);
    if (!info) return;
    info.eventSource.addEventListener(eventType, (event: MessageEvent) => {
      this.dispatchEvent(connectionKey, eventType, event.data);
    });
  }

  private dispatchEvent(connectionKey: string, eventType: string, rawData: string): void {
    // Any message arriving at all — parsed or not — proves the connection
    // is currently healthy, so consecutive-error tracking resets here
    // rather than only on the (rarer) 'disconnecting' scope-timeout event.
    // A malformed payload is a data-quality issue, not a transport failure,
    // so it still counts as proof the connection itself is fine.
    const info = this.connections.get(connectionKey);
    if (info) {
      info.consecutiveErrorCount = 0;
    }

    try {
      const parsedData = JSON.parse(rawData);
      this.eventsSubject.next({ connectionKey, type: eventType, data: parsedData });
    } catch {
      // Dropping malformed payloads instead of forwarding the raw string,
      // so subscribers always get typed objects.
      console.warn(`[SseService] Dropped malformed payload for event "${eventType}"`);
    }
  }
}
